import type Guid from "_shared/guids/guid";
import type { PermissionKind } from "permissions/_api/permission-kinds-api-service";
import { resourceForKind } from "permissions/_views/grant-row/use-grant-changes";
import { resourceLevel } from "permissions/_views/grant-row/grant-row-model";
import { grantOrigin, targetIdOf } from "permissions/_views/people/person-record-grants-model";
import { type GrantLevel, contributingGrants } from "permissions/_views/wording/permission-wording";
import type Permission from "permissions/permission";
import { resourceFromJSON } from "permissions/resources/permission-resource-factory";
import type { Resource } from "permissions/resources/permission-resource";
import { AccessLevel, type AnyResourceJSON } from "permissions/resources/permission-resource-types";

/**
 * What the record page lists: one row per person who holds a grant on the record.
 *
 * Plain functions, so the rules — which grant a row draws, how a search narrows the rows — read and
 * test apart from the page. The design is `docs/design/permissions/PERMISSIONS_UI_DESIGN.md`, "The
 * record page" and "The grant row".
 */

/** One row: a person, the grant the row draws, and every grant of theirs on the record. */
export type RecordHolderRow = Readonly<{
  key: string;
  granteeUserId: Guid;
  /** The strongest live grant, or the latest removed one when the person holds nothing live. */
  primary: Permission;
  /** Every grant the person holds on the record, live first and strongest first. More than one gets a disclosure. */
  grants: readonly Permission[];
}>;

function strength(permission: Permission): number {
  return resourceLevel(permission.resource).accessLevel ?? AccessLevel["Read-Only"];
}

/**
 * The record's grants, one row per person in the order the read returned them. A person with more
 * than one grant — Messaging writes a derived conversation grant beside a direct one — is one row
 * drawing the strongest live grant, with the rest behind its disclosure. A person whose every grant is
 * removed keeps a row, so it can be granted again, but is not counted by the caption.
 */
export function groupHolderGrants(grants: readonly Permission[]): RecordHolderRow[] {
  const byPerson = new Map<string, Permission[]>();
  for (const grant of grants) {
    const key = grant.granteeUserId.value.toLowerCase();
    const own = byPerson.get(key);
    if (own) own.push(grant);
    else byPerson.set(key, [grant]);
  }
  return [...byPerson.entries()].map(([key, own]) => {
    const sorted = [...own].sort((left, right) => {
      if (left.isDisabled !== right.isDisabled) return left.isDisabled ? 1 : -1;
      return strength(right) - strength(left);
    });
    return { key: `holder-${key}`, granteeUserId: sorted[0].granteeUserId, primary: sorted[0], grants: sorted };
  });
}

/** Whether the person still holds a live grant on the record. */
export function holdsLiveGrant(row: RecordHolderRow): boolean {
  return row.grants.some((grant) => !grant.isDisabled);
}

/** The disclosure on a row more than one grant opens: "Direct + from communications (2 grants)". */
export function holderDisclosure(row: RecordHolderRow, kinds: readonly PermissionKind[]): string | undefined {
  if (row.grants.length < 2) return undefined;
  return contributingGrants(row.grants.map((grant) => grantOrigin(grant, kinds)));
}

/**
 * The rows "Find a person" leaves: those whose name or email holds every word typed, ignoring case.
 * An empty search leaves every row.
 */
export function filterHolderRows(
  rows: readonly RecordHolderRow[],
  query: string,
  describe: (row: RecordHolderRow) => string,
): RecordHolderRow[] {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return [...rows];
  return rows.filter((row) => {
    const text = describe(row).toLowerCase();
    return words.every((word) => text.includes(word));
  });
}

/**
 * The resource a new grant of a per-record `kind` on `targetId` carries: the kind's containment and
 * level, as the person page's one-click Grant sends them, with the record named in the property the
 * kinds endpoint gives.
 *
 * A proposal's or a work agreement's grant names the fields it confers, and the server refuses one
 * with no `fields` at all (#6172). A new one names none: it opens the record, whose read asks for no
 * field, and its row sets the fields afterwards.
 */
export function recordResource(kind: PermissionKind, level: GrantLevel | undefined, targetId: string): Resource {
  const json = { ...(resourceForKind(kind, level).toJSON() as Record<string, unknown>) };
  if (kind.targetIdProperty) json[kind.targetIdProperty] = targetId;
  if (kind.controls.some((control) => control.name === "fields") && json.fields === undefined) json.fields = {};
  return resourceFromJSON(json as AnyResourceJSON);
}

/** Whether a grant is on `targetId`, compared as the server spells ids, without regard to case. */
export function isOnRecord(kind: PermissionKind, permission: Permission, targetId: string): boolean {
  return targetIdOf(kind, permission).toLowerCase() === targetId.toLowerCase();
}
